import { Graphics, Container } from "pixi.js";
import { CardBackgroundPattern } from "./CardBackgroundPattern";

/**
 * BackgroundPattern - Creates beautiful background patterns for the game
 * Low coupling: Independent utility class that can be used anywhere
 */
export class BackgroundPattern {
  /**
   * Creates a felt texture pattern for the game board (casino table feel)
   * @param colorScheme Color scheme name: 'green', 'blue', 'red', 'purple'
   */
  static createFeltPattern(width: number, height: number, colorScheme: string = 'green'): Graphics {
    const pattern = new Graphics();
    pattern.name = "FeltPattern";

    // Color schemes for different backgrounds
    const colorSchemes: Record<string, { base: number; dark: number; accent: number }> = {
      green: { base: 0x0a5d2e, dark: 0x0d4a24, accent: 0x1a7a47 },
      blue: { base: 0x1a4d7a, dark: 0x0f3d5c, accent: 0x2a6ba0 },
      red: { base: 0x7a1a1a, dark: 0x5c0f0f, accent: 0x9a2a2a },
      purple: { base: 0x4a1a5c, dark: 0x3d0f4a, accent: 0x6a2a7a },
    };

    const colors = colorSchemes[colorScheme] || colorSchemes.green;

    // Base felt color
    pattern.rect(0, 0, width, height);
    pattern.fill(colors.base);

    // Create subtle texture with diagonal lines
    const lineSpacing = 4;
    const lineColor = colors.dark;
    const lineAlpha = 0.3;

    // Diagonal lines pattern
    for (let i = -height; i < width + height; i += lineSpacing) {
      pattern.moveTo(i, 0);
      pattern.lineTo(i + height, height);
      pattern.stroke({ width: 1, color: lineColor, alpha: lineAlpha });
    }

    // Add subtle crosshatch pattern
    const crossSpacing = 8;
    for (let x = 0; x < width; x += crossSpacing) {
      pattern.moveTo(x, 0);
      pattern.lineTo(x, height);
      pattern.stroke({ width: 0.5, color: lineColor, alpha: 0.15 });
    }
    for (let y = 0; y < height; y += crossSpacing) {
      pattern.moveTo(0, y);
      pattern.lineTo(width, y);
      pattern.stroke({ width: 0.5, color: lineColor, alpha: 0.15 });
    }

    // Add subtle radial gradient effect (simulated with circles)
    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.max(width, height) * 0.7;
    
    for (let radius = maxRadius; radius > 0; radius -= 50) {
      const alpha = (maxRadius - radius) / maxRadius * 0.1;
      pattern.circle(centerX, centerY, radius);
      pattern.stroke({ width: 2, color: colors.accent, alpha });
    }

    return pattern;
  }

  /**
   * Creates CSS background pattern string for HTML page
   */
  static createPageBackgroundCSS(): string {
    return `
      background: 
        radial-gradient(circle at 20% 30%, rgba(15, 32, 39, 0.8) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(32, 58, 67, 0.8) 0%, transparent 50%),
        radial-gradient(circle at 50% 50%, rgba(44, 83, 100, 0.6) 0%, transparent 70%),
        linear-gradient(135deg, #0f2027 0%, #203a43 25%, #2c5364 50%, #203a43 75%, #0f2027 100%);
      background-size: 100% 100%, 100% 100%, 100% 100%, 100% 100%;
      background-attachment: fixed;
      position: relative;
    `;
  }

  /**
   * Creates an advanced page background with animated pattern
   */
  static createAdvancedPageBackgroundCSS(): string {
    return `
      background: 
        repeating-linear-gradient(
          45deg,
          rgba(15, 32, 39, 0.1) 0px,
          rgba(15, 32, 39, 0.1) 2px,
          transparent 2px,
          transparent 4px
        ),
        repeating-linear-gradient(
          -45deg,
          rgba(32, 58, 67, 0.1) 0px,
          rgba(32, 58, 67, 0.1) 2px,
          transparent 2px,
          transparent 4px
        ),
        radial-gradient(circle at 20% 30%, rgba(15, 32, 39, 0.8) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(32, 58, 67, 0.8) 0%, transparent 50%),
        radial-gradient(circle at 50% 50%, rgba(44, 83, 100, 0.6) 0%, transparent 70%),
        linear-gradient(135deg, #0f2027 0%, #203a43 25%, #2c5364 50%, #203a43 75%, #0f2027 100%);
      background-size: 
        100px 100px,
        100px 100px,
        100% 100%,
        100% 100%,
        100% 100%,
        100% 100%;
      background-attachment: fixed;
      position: relative;
    `;
  }

  /**
   * Creates a subtle border pattern for the game board with rounded corners
   */
  static createBorderPattern(width: number, height: number, borderWidth: number = 4): Graphics {
    const border = new Graphics();
    border.name = "BorderPattern";

    // Use same corner radius as cards for consistency
    const cornerRadius = CardBackgroundPattern.CORNER_RADIUS;
    const borderColor = 0x1a7a47;
    const borderColorDark = 0x0a3d1e;

    // Create rounded border by drawing outer rounded rect and inner rounded rect
    // Outer border (filled)
    border.roundRect(0, 0, width, height, cornerRadius);
    border.fill({ color: borderColorDark, alpha: 0.8 });
    
    // Inner cutout to create border effect (with slightly smaller corner radius)
    const innerRadius = Math.max(0, cornerRadius - borderWidth);
    border.roundRect(borderWidth, borderWidth, width - borderWidth * 2, height - borderWidth * 2, innerRadius);
    border.fill({ color: 0x0a5d2e }); // Match felt background color

    // Inner highlight lines (with rounded corners)
    const highlightWidth = 1;
    const highlightOffset = borderWidth + highlightWidth;
    
    // Top highlight
    border.roundRect(highlightOffset, highlightOffset, width - highlightOffset * 2, highlightWidth, innerRadius);
    border.fill({ color: borderColor, alpha: 0.5 });
    
    // Bottom highlight
    border.roundRect(highlightOffset, height - highlightOffset - highlightWidth, width - highlightOffset * 2, highlightWidth, innerRadius);
    border.fill({ color: borderColor, alpha: 0.5 });

    return border;
  }
}

